import { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { tariffRatesApi } from '../../api/tariffRates';
import AddMaterialDialog from './AddMaterialDialog';

export default function AddTariffRateDialog({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    country_from: '',
    country_to: '',
    hts_code: '',
    material_id: '',
    rate_percentage: '',
    effective_date: new Date().toISOString().split('T')[0],
    end_date: '',
    notes: ''
  });
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);

  useEffect(() => {
    if (open) {
      fetchMaterials();
    }
  }, [open]);

  const fetchMaterials = async () => {
    try {
      const response = await tariffRatesApi.getMaterials();
      if (response.success) {
        setMaterials(response.data);
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await tariffRatesApi.createTariffRate(formData);
      if (response.success) {
        onSuccess();
        setFormData({
          country_from: '',
          country_to: '',
          hts_code: '',
          material_id: '',
          rate_percentage: '',
          effective_date: new Date().toISOString().split('T')[0],
          end_date: '',
          notes: ''
        });
      } else {
        alert(response.error || 'Failed to create tariff rate');
      }
    } catch (error) {
      console.error('Error creating tariff rate:', error);
      alert('Failed to create tariff rate');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-xl font-semibold">Add Tariff Rate</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Country *
                </label>
                <Input
                  name="country_from"
                  value={formData.country_from}
                  onChange={handleChange}
                  placeholder="e.g., CN, MX, US"
                  maxLength={2}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Country *
                </label>
                <Input
                  name="country_to"
                  value={formData.country_to}
                  onChange={handleChange}
                  placeholder="e.g., CN, MX, US"
                  maxLength={2}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HTS Code *
              </label>
              <Input
                name="hts_code"
                value={formData.hts_code}
                onChange={handleChange}
                placeholder="e.g., 7208.51.00"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Material
              </label>
              <div className="flex gap-2">
                <select
                  name="material_id"
                  value={formData.material_id}
                  onChange={handleChange}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Select a material (optional)</option>
                  {materials.map(material => (
                    <option key={material.id} value={material.id}>
                      {material.name} {material.category && `(${material.category})`}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddMaterial(true)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate Percentage *
              </label>
              <Input
                name="rate_percentage"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.rate_percentage}
                onChange={handleChange}
                placeholder="e.g., 25.00"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective Date *
                </label>
                <Input
                  name="effective_date"
                  type="date"
                  value={formData.effective_date}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <Input
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="e.g., Cost: $1.10/kg, Steel (Hot-rolled)"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Tariff Rate'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Add Material Dialog */}
      <AddMaterialDialog
        open={showAddMaterial}
        onClose={() => setShowAddMaterial(false)}
        onSuccess={() => {
          setShowAddMaterial(false);
          fetchMaterials();
        }}
      />
    </>
  );
}
